/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Combobox, Control, Label, Input, ComboboxOption, PageLoader, Badge } from "@forge/common";

//@Components
import { DataCardTable } from "./dataCardTable";
import ConfirmationModal from "../../../reusablecomponents/confirmationmodal";

//@Models
import { Site } from "../../../models/devicelist";
import { DeviceDetails, OverviewTabProps } from "../../../models/viewdevice";

//@Utils
import { comboOptionValue } from "../../../utils/staticdata";
import { LOCALE_CONST, VIEW_DEVICE_OVERVIEW } from "../../../utils/constants";
import { extractNameplateValues, formatNameplateData, formatDvmLicenseDetails, convertUTCStringtoFormattedDate } from "../../../utils/utility";
import { launchDarklyFeatureToggleKey } from "../../../launchDarklyConfig";

//@Styles
import "./styles.scss";

//@Functional component
const OverviewTab: React.FC<OverviewTabProps> = ({ data, isLoading, versionDetails, gatewayType, installedVersion }) => {
  const { t } = useTranslation();
  const flags = useFlags();

  // Helper function to get tag display text
  const getTagDisplayText = (tag: string) => {
    if (!tag) return null;
    
    // Don't display badge for forgeedge (both raw API value and formatted text)
    if (tag?.toLowerCase() === 'forgeedge' || tag?.toLowerCase() === 'forge edge') return null;
    
    switch (tag?.toLowerCase()) {
      case 'legacy':
        return t(LOCALE_CONST.legacy);
      case 'forgelegacy':
        return t(LOCALE_CONST.forgelegacy);
      case 'azure':
        return t(LOCALE_CONST.fc); // "FC" as per requirement
      case 'fc':
        return t(LOCALE_CONST.fc);
      default:
        return tag; // Return the tag as-is since API provides formatted text
    }
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const nameplateValues = extractNameplateValues(versionDetails, flags.useSupervisorNestedProperties || false);
  //handle the configuration modal close
  const handleConfigurationClose = () => setIsConfirmationModalOpen(false);

  //render the name plate UI for basic info
  const renderNameplateUI = (gwType: string) => {
    const formattedData = formatNameplateData(t, nameplateValues[0], data, gwType);
    return <DataCardTable deviceData={formattedData} title={t(LOCALE_CONST.basicInfo)} />;
  };

  //get basic info data for EBI-Cognition gateway type
  const getEBICognitionBasicInfo = (record: DeviceDetails, softwareVersion?: string) => {
    if (isLoading) {
      return [];
    }

    const isConnected = record?.connectivity?.toLowerCase() === 'connected';
    
    const basicFields = [
      {
        label: t(LOCALE_CONST.name),
        value: record?.name || "-"
      },
      {
        label: t(LOCALE_CONST.gatewayTypeLower),
        value: record?.gatewayType || "-"
      },
      {
        label: t(LOCALE_CONST.model),
        value: record?.model || "-"
      },
      {
        label: t(LOCALE_CONST.hardwareID),
        value: record?.hardwareId || "-"
      }
    ];

    // Add System GUID field only if connectivity is "Connected"
    if (isConnected) {
      basicFields.push({
        label: t(LOCALE_CONST.systemGUID),
        value: record?.deviceId || "-"
      });
    }

    basicFields.push({
      label: t(LOCALE_CONST.connectivityType),
      value: record?.connectivity || "-"
    });

    basicFields.push({
      label: t(LOCALE_CONST.softwareVersion),
      value: softwareVersion || "-"
    });
    return basicFields;
  };

  //Publish frequency options UI component
  const PublishFrequencyOptions = () => {
    return (
      <div className="publish-frequency-container">
        <Control className="w-[15rem] mt-2" spacingAbove spacingBelow>
          <Label
            className="font-normal iot-label-name-txt-cls"
            required
            requiredText=""
          >
            {VIEW_DEVICE_OVERVIEW.PUBLISH_FREQUENCY}
          </Label>
          <Input
            id={"publish-frequency-value"}
            className="frequency-input-box"
          />
        </Control>
        <Control className="w-[20rem] mt-2" spacingAbove spacingBelow>
          <Label className="font-normal iot-label-name-txt-cls"></Label>
          <Combobox
            debounce={300}
            height={10}
            i18nClear="Clear"
            i18nClearSelectedOptions="Clear selected options"
            i18nNoResults="No results"
            id="frequency-combobox"
            minCharacters={3}
            onBlur={function noRefCheck() { }}
            onChange={function noRefCheck() { }}
            onFocus={function noRefCheck() { }}
            placeholder={VIEW_DEVICE_OVERVIEW.SELECT_AN_OPTION}
            size="large"
            className="frequency-combobox"
          >
            {comboOptionValue.map((item) => (
              <ComboboxOption label={item.label} value={item.value} />
            ))}
          </Combobox>
        </Control>
      </div>
    );
  };
  
  const getSiteName = (siteRec: Site[]) =>
    siteRec?.map((item: Site) => item?.name)?.join(", ") || "-";

  const getAssociationData = (record: DeviceDetails) => {
    if (isLoading) {
      return [];
    } else {
      return [
        {
          label: t(LOCALE_CONST.organization),
          value: record?.organizationName || ""
        },
        { 
          type: "site",
          label: t(LOCALE_CONST.associatedSites), 
          value: getSiteName(record?.site || []) 
        }
      ];
    }
  };

  const getSystemInformationData = (record: DeviceDetails) => {
    if (isLoading) {
      return [];
    } else {
      return [
        {
          label: t(LOCALE_CONST.name),
          value: record?.name || "-"
        },
        {
          label: t(LOCALE_CONST.id),
          value: record?.deviceId || "-"
        },
        {
          label: t(LOCALE_CONST.healthStatus),
          value: record?.healthStatus || "-"
        },
        {
          label: t(LOCALE_CONST.serialNumber),
          value: record?.serialNumber || "-"
        },
        {
          label: t(LOCALE_CONST.platformVersion),
          value: record?.platformVersion || "-"
        },
        {
          label: t(LOCALE_CONST.updatedAt),
          value: convertUTCStringtoFormattedDate(record?.updatedAt) || "-"
        }
      ];
    }
  };

  // Check if provider is forgelegacy
  const isForgeLegacy = data?.tag?.toLowerCase() === 'forgelegacy' || 
                        data?.providerName?.toLowerCase() === 'forgelegacy';

  // Check if system information feature flag is enabled
  const isSystemInfoEnabled = flags[launchDarklyFeatureToggleKey.ENABLE_FC_SOFTWARE_DETAILS];

  // Check if gatewayType is EBI-Cognition
  const isEBICognition = gatewayType === 'EBI-Platform';

  return (
    <>
      <PageLoader isLoading={isLoading} />
      {isEBICognition ? (
        <DataCardTable 
          deviceData={getEBICognitionBasicInfo(data, installedVersion)} 
          title={t(LOCALE_CONST.basicInfo)} 
        />
      ) : (
        renderNameplateUI(gatewayType)
      )}
      {gatewayType === "DVM" && (
        <DataCardTable
          deviceData={formatDvmLicenseDetails(t, nameplateValues[0])}
          title={t(LOCALE_CONST.licenseDetails)}
        />
      )}
      {isForgeLegacy && isSystemInfoEnabled && (
        <DataCardTable 
          deviceData={getSystemInformationData(data)} 
          title={(() => {
            const tagValue = data?.tag || data?.providerName;
            const tagDisplayText = tagValue ? getTagDisplayText(tagValue) : null;
            
            return tagDisplayText 
              ? `${t(LOCALE_CONST.systemInfo)} (${tagDisplayText})`
              : t(LOCALE_CONST.systemInfo);
          })()}
          isCollapsible={true}
          defaultExpanded={true}
        />
      )}
      <DataCardTable deviceData={getAssociationData(data)} title={t(LOCALE_CONST.registrationDetails)} />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfigurationClose}
        onConfirm={handleConfigurationClose}
        title={VIEW_DEVICE_OVERVIEW.CONFIGURATION}
        content={<PublishFrequencyOptions />}
        footerRightText={VIEW_DEVICE_OVERVIEW.SAVE}
      />
    </>
  );
};

export default OverviewTab;
